import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLaunch, marketplaceOpen } from "@/context/LaunchContext";
import ContentSkeleton from "@/components/ContentSkeleton";

// Session-level latch: once auth + launch config have resolved at least once
// in this page load, we NEVER revert an already-rendered page to a full-page
// skeleton (e.g. on a background refresh, a re-mount across a route change, or
// a bfcache re-render). This prevents the "real content → skeleton → real
// content" flash. The branded skeleton is only shown on the very first,
// pre-resolution paint (inside the Vanturis shell), which is an acceptable
// "stable placeholder → real content" startup.
let _launchResolvedOnce = false;

/** Wraps private routes. If launch mode is coming_soon/private_preview and the
 *  user is not authorized, sends them to the public landing page. When the
 *  site is on public_launch, requireAuth-fail routes send the visitor to the
 *  public /login page; before public_launch we keep sending them to the
 *  private /internal-access route so external sign-in remains gated. */
export default function LaunchGate({ children, requireAuth = true }) {
  const { user, loading: authLoading } = useAuth();
  const { config, loading: launchLoading } = useLaunch();
  const loading = authLoading || launchLoading;

  if (!loading) _launchResolvedOnce = true;

  // Still bootstrapping AND nothing has ever resolved yet → branded skeleton
  // inside the already-visible shell (never a blank frame or generic Loading).
  if (loading && !_launchResolvedOnce) {
    return <ContentSkeleton testid="launch-gate-skeleton" />;
  }

  // If a background refresh flips loading true after we've resolved once, keep
  // the current page rendered rather than yanking it back to a skeleton.
  if (loading && _launchResolvedOnce) {
    return children;
  }

  const open = marketplaceOpen(config, user);
  if (!open) return <Navigate to="/" replace />;
  if (requireAuth && !user) {
    const authRoute = config?.launch_mode === "public_launch" ? "/login" : "/internal-access";
    return <Navigate to={authRoute} replace />;
  }
  return children;
}
